import { createFileRoute } from "@tanstack/react-router";
import { serviceSchemaScripts } from "@/lib/service-schema";
import { freshnessMeta, webPageSchemaScript } from "@/lib/page-freshness";
import { FaqSection, faqSchemaScript, type FaqItem } from "@/components/landing/FaqSection";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { ParticleBackground } from "@/components/landing/ParticleBackground";
import { PageIntro } from "@/components/landing/PageIntro";
import { CustomBuildsSection } from "@/components/landing/CustomBuildsSection";
import { TestimonialsSection } from "@/components/landing/TestimonialsSection";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { ClosingCTA } from "@/components/landing/ClosingCTA";
import { RelatedServices } from "@/components/landing/RelatedServices";
import raceAiBackdropAsset from "@/assets/raceai-iphone-mockup.webm";
import raceAiPosterAsset from "@/assets/raceai-poster.webp.asset.json";

const PUBLISHED = "2026-08-29T00:00:00+00:00";
const MODIFIED = "2026-09-03T00:00:00+00:00";

const TITLE = "Custom App Builds & Full Product Development | Square Migration";
const DESCRIPTION =
  "Full custom app builds with real accounts, data, and logic, sales enablement apps, lead qualifiers, niche utilities, and complete products shipped fast.";

const FAQS: FaqItem[] = [
  {
    "question": "What counts as a custom app build versus a lead-gen tool?",
    "answer": "If it needs accounts, a database, roles or an admin area, it's a product build. Single-purpose calculators and assessments are lightweight tools."
  },
  {
    "question": "How do custom builds get scoped?",
    "answer": "We strip the idea to the one workflow it has to nail, model data and access, then ship a working v1 your users can log into, not a clickable prototype."
  },
  {
    "question": "Can you add billing, mobile support or analytics later?",
    "answer": "Yes. After v1 we iterate based on real usage, layering in billing, analytics, mobile polish and additional modules."
  },
  {
    "question": "Do we own the application?",
    "answer": "Yes. The app, its code and its data are yours, hosted on your domain."
  }
];

export const Route = createFileRoute("/custom-app-builds")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://www.squaremigration.com/custom-app-builds" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://www.squaremigration.com/og-image.jpg" },
      { name: "twitter:image", content: "https://www.squaremigration.com/og-image.jpg" },
      ...freshnessMeta({ published: PUBLISHED, modified: MODIFIED }),
    ],
    links: [{ rel: "canonical", href: "https://www.squaremigration.com/custom-app-builds" }],
    scripts: [...serviceSchemaScripts({
      path: "/custom-app-builds",
      name: "Custom App Builds",
      description: DESCRIPTION,
      serviceType: "Custom web application development",
    }), faqSchemaScript(FAQS), webPageSchemaScript({
      path: "/custom-app-builds",
      name: TITLE,
      description: DESCRIPTION,
      published: PUBLISHED,
      modified: MODIFIED,
    })],
  }),
  component: CustomAppBuildsPage,
});

function CustomAppBuildsPage() {
  return (
    <main className="relative">
      <ParticleBackground />
      <Header />
      <PageIntro
        eyebrow="Full App Builds"
        title="Custom apps with real accounts, data, and logic."
        description="When a lightweight widget isn't enough, we build the full product: authentication, databases, dashboards, and integrations, scoped to launch fast and grow with you."
        bullets={["Sales enablement apps", "Lead qualifiers", "Niche utility apps", "Internal dashboards"]}
        backgroundVideo={{
          src: raceAiBackdropAsset,
          poster: raceAiPosterAsset.url,
          ariaLabel: "Race AI racing analytics app shown on a floating phone",
        }}
      />
      <CustomBuildsSection
        eyebrow="Shipped Products"
        headline="Real software, with users and a database behind it."
        lead="These are the builds that outgrew a single page: subscriptions, logins, live data, mobile installs and admin tooling. Same speed, bigger surface area."
      />
      <TestimonialsSection />
      <ProcessSection
        eyebrow="Build Approach"
        title="We ship a usable version early, then grow it."
        lead="Rather than a six-month spec, we get a working v1 in front of real users and let their behavior decide what gets built next."
        steps={[
          { number: "01", title: "Define the Core Job", description: "Strip the idea down to the one workflow the product has to nail first." },
          { number: "02", title: "Model Data & Access", description: "Accounts, roles, permissions and the schema everything else will hang off of." },
          { number: "03", title: "Ship a Working v1", description: "A real, hosted app your users can log into, not a clickable prototype." },
          { number: "04", title: "Iterate on Usage", description: "Add billing, analytics, mobile polish and new modules based on what users actually do." },
        ]}
      />
      <ClosingCTA
        eyebrow="Bigger Than a Widget?"
        headline={
          <>
            If it needs logins, data and <span className="text-primary">a roadmap</span>
          </>
        }
        subhead="That’s a product build."
        body="Bring the concept, internal tool, client-facing platform, or a product you want to sell. We’ll map the first version, what it takes to ship, and what can wait."
        ctaLabel="Scope a Product Build"
      />
      <FaqSection items={FAQS} />
      <RelatedServices current="/custom-app-builds" />
      <Footer />
    </main>
  );
}
